import { Heading } from '@snsw/react-component-library';
import { GelTheme } from 'components/GelTheme';
import styled, { css } from 'styled-components/macro';
import { ReactNode } from 'react';

export interface NamedValue {
  name?: string | ReactNode;
  value?: string | (string | ReactNode)[] | ReactNode;
}

export type SummaryResponseItem = string | string[] | NamedValue;

export interface SummaryResponseListProps {
  title?: string;
  items: SummaryResponseItem[];
  separators?: boolean;
}

const Container = styled.div`
  margin-bottom: ${GelTheme.spacing(0.5)};
`;

const Title = styled(Heading)<{ separators?: boolean }>`
  margin: 0 0 ${(props) => (props.separators ? GelTheme.spacing(1) : GelTheme.spacing(1.5))};
`;

const SummaryResponseItems = styled.div<{ separators?: boolean }>`
  display: flex;
  flex-direction: column;

  ${(props) =>
    !props.separators &&
    css`
      gap: ${GelTheme.spacing(1.5)};
    `};
`;

const SummaryResponseEntry = styled.div<{ separators?: boolean }>`
  display: flex;
  flex-direction: column;
  line-height: ${GelTheme.spacing(1.5)};

  ${(props) =>
    props.separators &&
    css`
      border-bottom: 1px solid ${GelTheme.color('geyser')};
      padding: ${GelTheme.spacing(1)} 0;

      &:first-of-type {
        border-top: 1px solid ${GelTheme.color('geyser')};
      }
    `}
`;

const EntryTitle = styled.span`
  font-weight: ${GelTheme.fontWeight('semi-bold')};
  padding-bottom: ${GelTheme.spacing(0.5)};
`;

const UL = styled.ul`
  margin: 0;

  li:not(:last-of-type) {
    margin-bottom: ${GelTheme.spacing(0.5)};
  }
`;

export const SummaryResponseList = ({ title, items, separators }: SummaryResponseListProps) => {
  const namedValues = items.map(getNamedValue);

  return (
    <Container>
      {title && (
        <Title level={3} separators={separators}>
          {title}
        </Title>
      )}

      <SummaryResponseItems separators={separators}>
        {namedValues.map(({ name, value }, index) => (
          <SummaryResponseEntry key={index} separators={separators}>
            <EntryTitle>{name}</EntryTitle>
            {!Array.isArray(value) && <span>{value}</span>}

            {Array.isArray(value) && (
              <UL>
                {value.map((v, index) => (
                  <li key={index}>{v}</li>
                ))}
              </UL>
            )}
          </SummaryResponseEntry>
        ))}
      </SummaryResponseItems>
    </Container>
  );
};

function getNamedValue(item: SummaryResponseItem): NamedValue {
  if (!item) return {};

  if (Array.isArray(item)) {
    if (!item.length) return {};

    if (item.length === 2) {
      const [name, value] = item;
      return { name, value };
    } else {
      const [name, ...value] = item;
      return { name, value };
    }
  }

  if (typeof item === 'string') {
    return { name: item };
  }

  return item as NamedValue;
}
